import React from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { LanguageProvider } from './i18n/LanguageContext'
import { getCanonicalServiceKey, getLocalizedServiceSlug, getEquivalentPath } from './i18n/routes'
import Header from './components/Header'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'

import HomePage from './pages/HomePage'
import ServicesPage from './pages/ServicesPage'
import ServiceDetailPage from './pages/ServiceDetailPage'
import GalleryPage from './pages/GalleryPage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import NotFoundPage from './pages/NotFoundPage'
import ForbiddenPage from './pages/ForbiddenPage'

/**
 * Dynamic redirect component for services routes with slug mapping
 */
function ServiceSlugRedirect({ targetLang }) {
  const { slug } = useParams()
  const key = getCanonicalServiceKey(slug)
  const localizedSlug = getLocalizedServiceSlug(key, targetLang)
  const targetPath = targetLang === 'en' 
    ? `/en/services/${localizedSlug}` 
    : `/transport/${localizedSlug}`
  return <Navigate to={targetPath} replace />
}

/**
 * Generic redirect component that computes the equivalent path for the requested target language
 */
function GenericPathRedirect({ targetLang }) {
  const location = useLocation()
  const target = getEquivalentPath(location.pathname, targetLang)
  return <Navigate to={target} replace />
}

export default function App() {
  return (
    <LanguageProvider>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-sky-500 selection:text-white antialiased">
        <Header />
        <main className="flex-grow">
          <Routes>
            {/* ========================================================
                1. DANISH (PRIMARY DEFAULT) ROUTES (without language prefix)
                ======================================================== */}
            <Route path="/" element={<HomePage />} />
            <Route path="/transport" element={<ServicesPage />} />
            <Route path="/transport/:slug" element={<ServiceDetailPage />} />
            <Route path="/om-mrou-transport" element={<AboutPage />} />
            <Route path="/galleri" element={<GalleryPage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/403" element={<ForbiddenPage />} />

            {/* ========================================================
                2. ENGLISH (SECONDARY) ROUTES (under /en prefix)
                ======================================================== */}
            <Route path="/en" element={<HomePage />} />
            <Route path="/en/services" element={<ServicesPage />} />
            <Route path="/en/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/en/about-us" element={<AboutPage />} />
            <Route path="/en/gallery" element={<GalleryPage />} />
            <Route path="/en/contact" element={<ContactPage />} />
            <Route path="/en/403" element={<ForbiddenPage />} />

            {/* ========================================================
                3. SEO 301 REDIRECTS (Legacy / Alternative URLs)
                ======================================================== */}
            {/* Danish redirects */}
            <Route path="/om-os" element={<Navigate to="/om-mrou-transport" replace />} />
            <Route path="/ydelser" element={<Navigate to="/transport" replace />} />
            <Route path="/ydelser/:slug" element={<ServiceSlugRedirect targetLang="da" />} />
            <Route path="/da" element={<Navigate to="/" replace />} />
            <Route path="/da/*" element={<GenericPathRedirect targetLang="da" />} />

            {/* English redirects / aliases */}
            <Route path="/en/transport" element={<Navigate to="/en/services" replace />} />
            <Route path="/en/transport/:slug" element={<ServiceSlugRedirect targetLang="en" />} />
            <Route path="/en/ydelser" element={<Navigate to="/en/services" replace />} />
            <Route path="/en/ydelser/:slug" element={<ServiceSlugRedirect targetLang="en" />} />
            <Route path="/en/about" element={<Navigate to="/en/about-us" replace />} />
            <Route path="/en/om-os" element={<Navigate to="/en/about-us" replace />} />
            <Route path="/en/om-mrou-transport" element={<Navigate to="/en/about-us" replace />} />
            <Route path="/en/kontakt" element={<Navigate to="/en/contact" replace />} />
            <Route path="/en/galleri" element={<Navigate to="/en/gallery" replace />} />

            {/* 404 Catch-All */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </LanguageProvider>
  )
}
